import { nanoid } from "nanoid";
import { ThemeObserver } from "./ThemeObserver";
import { RouteUtil } from "./RouteUtil";
import { LocaleUtil } from "./LocaleUtil";
import { ThemeInjector } from "./ThemeInjector";

import type { FloatSettings } from "../controllers/FloatingUiController";

export class ThemeHelper {
  route: RouteUtil;
  observer: ThemeObserver;
  locale: LocaleUtil;
  watch: ThemeObserver["watch"];

  private _rootUrl: string;

  constructor({
    rootUrl,
    wishlistUrl,
  }: {
    rootUrl: string;
    wishlistUrl: string;
  }) {
    this._rootUrl = rootUrl;
    this.route = new RouteUtil({ wishlistUrl });
    this.observer = new ThemeObserver({ routeUtil: this.route });
    this.locale = new LocaleUtil();
    this.watch = this.observer.watch.bind(this.observer);
  }

  getPageType(path: string = window.location.pathname) {
    return this.route.parse(path).pageType;
  }

  getWishlistId(url: string | URL) {
    if (!url) {
      return null;
    }

    if (url instanceof URL) {
      url = url.pathname;
    }

    if (url.endsWith("/")) {
      url = url.substring(0, url.length - 1);
    }

    const parts = url.replace(this._rootUrl, "/").split("/");
    return parts.length === 4 ? parts.pop()! : "mine";
  }

  getProductHandle(url: string | URL) {
    if (!url) {
      return null;
    }

    if (url instanceof URL) {
      url = url.pathname;
    }

    if (url.endsWith("/")) {
      url = url.slice(0, -1);
    }

    return window.decodeURI(url).split("/").pop()?.split("?")[0]?.split("#")[0];
  }

  getVariantId(
    url: string | URL,
    formElement?: HTMLFormElement | ThemeInjector,
  ) {
    let variantId = null;

    if (url) {
      if (url instanceof URL) {
        url = url.pathname;
      }

      const params = new URLSearchParams(url.split("?")[1]);
      variantId = params.get("variant");
    }

    if (!variantId && formElement) {
      try {
        const formData = new FormData(formElement.element ?? formElement);
        variantId = formData.get("id");
      } catch (error) {
        console.warn("Failed to parse variant from product form.", error);
      }
    }

    return variantId;
  }

  translate(id: string, values?: Record<string, string>) {
    return this.locale.translate(id, values);
  }

  createComponent(
    name: string,
    config?: {
      dataset: Record<string, string>;
      props: Record<string, string | object>;
      wrap?: {
        name: Parameters<typeof document.createElement>[0];
        props?: any;
      };
    },
  ) {
    const element = document.createElement(name);

    if (config?.dataset) {
      Object.entries(config?.dataset).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        if (typeof value === "boolean") {
          element.dataset[key] = "";
        } else if (typeof value === "object") {
          element.dataset[key] = JSON.stringify(value);
        } else {
          element.dataset[key] = value;
        }
      });
    }

    if (config?.props) {
      Object.entries(config?.props).forEach(([key, value]) => {
        if (key === "floating" && value) {
          // Turn reference element into serialisable id selector
          const floating = value as FloatSettings;
          const referenceElement =
            floating?.reference instanceof ThemeInjector
              ? floating.reference.element
              : floating.reference;

          if (
            typeof referenceElement === "object" &&
            "id" in referenceElement
          ) {
            referenceElement.setAttribute("wk-id", nanoid());
            // @ts-ignore
            element[key] = {
              ...floating,
              reference: `[wk-id="${referenceElement.getAttribute("wk-id")}"]`,
            };
            return;
          }
        }

        // @ts-ignore
        element[key] = value;
      });
    }

    if (config?.wrap?.name) {
      const wrap = this.createElement(
        config.wrap.name,
        config.wrap.props,
        element,
      );
      wrap.setAttribute("wk-skip", "");
      return wrap;
    }

    return element;
  }

  createHeadlessComponent(
    name: string,
    config: {
      host: HTMLElement | ThemeInjector;
      dataset?: Record<string, string>;
    },
  ) {
    const host = (config.host as ThemeInjector).element
      ? (config.host as ThemeInjector).element
      : (config.host as HTMLElement);

    if (!host) {
      throw new Error("Headless host is undefined");
    }

    if (config.dataset) {
      Object.entries(config.dataset).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        if (typeof value === "boolean") {
          host.dataset[key] = "";
        } else if (typeof value === "object") {
          host.dataset[key] = JSON.stringify(value);
        } else {
          host.dataset[key] = value;
        }
      });
    }

    const ElementConstructor = window.headlessElements?.get(name);
    if (!ElementConstructor) {
      throw new Error(`Headless element not found: ${name}`);
    }

    return new ElementConstructor(host);
  }

  createElement(
    name: Parameters<typeof document.createElement>[0],
    props: any = {},
    ...children: (Node | string)[]
  ) {
    const element = Object.assign(
      document.createElement(name),
      props,
    ) as Element;

    if (children) {
      element.append(...children);
    }

    return element;
  }
}
